import { jsx as _jsx } from "react/jsx-runtime";
import { useRequiredAppUpdate } from './required-app-update.js';
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { buildIdentity, createUpdateMonitor, UPDATE_CHECK_INTERVAL_MS, UPDATE_SNOOZE_MS } from './app-update.js';
const RefreshSafetyContext = createContext(null);
export function RefreshSafetyProvider({ safety, children }) {
    return _jsx(RefreshSafetyContext.Provider, { value: safety, children: children });
}
/** Owning forms expose their real dirty/pending state; absent provider is harmless in isolated stories. */
export function useRefreshBlocker(blocked, explicitSafety) {
    const context = useContext(RefreshSafetyContext);
    const safety = explicitSafety ?? context;
    useLayoutEffect(() => blocked ? safety?.hold() : undefined, [blocked, safety]);
}
/** Mounted inside an open modal's portal, not its always-mounted root. */
export function RefreshBlocker() { useRefreshBlocker(true); return null; }
export function useAppUpdate({ running, account, load, safety, canRefresh, storageKey, compatibility, canRefreshRequired = canRefresh, reload = () => window.location.reload() }) {
    const required = useRequiredAppUpdate({ running, account, load, safety, canRefresh: canRefreshRequired, storageKey, reload, compatibility });
    const current = useRef({ account, canRefresh, reload });
    useLayoutEffect(() => { current.current = { account, canRefresh, reload }; });
    const [ownedMonitor, setMonitor] = useState(null);
    const safe = useSyncExternalStore(safety.subscribe, safety.isSafe, () => false);
    const available = useSyncExternalStore(subscribeAvailability, isAvailable, () => false);
    const identity = buildIdentity(running);
    const monitor = ownedMonitor?.account === account && ownedMonitor.identity === identity ? ownedMonitor.monitor : null;
    const observedAccount = useRef(null);
    useEffect(() => {
        if (!account || !identity) {
            if (observedAccount.current !== null) {
                try {
                    sessionStorage.removeItem(storageKey);
                }
                catch { /* storage is optional */ }
            }
            observedAccount.current = null;
            setMonitor(null);
            return;
        }
        observedAccount.current = account;
        let ignored = [];
        let snoozed = {};
        try {
            const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null');
            if (saved && typeof saved === 'object' && 'account' in saved && saved.account === account &&
                'running' in saved && saved.running === identity && 'ignored' in saved && Array.isArray(saved.ignored)) {
                ignored = saved.ignored.filter((value) => typeof value === 'string');
                if ('snoozed' in saved && saved.snoozed && typeof saved.snoozed === 'object' && !Array.isArray(saved.snoozed)) {
                    snoozed = Object.fromEntries(Object.entries(saved.snoozed).filter((entry) => typeof entry[1] === 'number' && Number.isFinite(entry[1]) && entry[1] > Date.now() && entry[1] <= Date.now() + UPDATE_SNOOZE_MS));
                }
            }
        }
        catch { /* denied/corrupt storage does not block use */ }
        const [release, gitSha, builtAt] = JSON.parse(identity);
        const save = () => {
            try {
                sessionStorage.setItem(storageKey, JSON.stringify({ account, running: identity, ignored, snoozed }));
            }
            catch { /* optional */ }
        };
        const next = createUpdateMonitor({ running: { release, gitSha, builtAt }, load, ignored, snoozed,
            persist: values => { ignored = values; save(); },
            persistSnoozed: values => { snoozed = values; save(); },
        });
        save();
        setMonitor({ account, identity, monitor: next });
        const check = () => {
            if (isAvailable())
                void next.check();
        };
        check();
        // Periodic polls must not be skipped by an off-cycle resume/event check.
        const timer = window.setInterval(() => {
            if (isAvailable())
                void next.check(true);
        }, UPDATE_CHECK_INTERVAL_MS);
        window.addEventListener('focus', check);
        window.addEventListener('pageshow', check);
        window.addEventListener('online', check);
        document.addEventListener('visibilitychange', check);
        return () => {
            next.dispose();
            clearInterval(timer);
            window.removeEventListener('focus', check);
            window.removeEventListener('pageshow', check);
            window.removeEventListener('online', check);
            document.removeEventListener('visibilitychange', check);
        };
    }, [account, identity, load, storageKey]);
    const version = useSyncExternalStore(monitor?.subscribe ?? noSubscribe, monitor?.getSnapshot ?? emptySnapshot, emptySnapshot);
    const allowed = () => !!account && current.current.account === account && safety.isSafe() &&
        isAvailable() && current.current.canRefresh();
    return {
        ...{ ...(required ? { required } : {}) },
        builds: identity && version ? { current: parseIdentity(identity), available: parseIdentity(version) } : null,
        visible: !required && !!version && safe && !!account && canRefresh() && available,
        later: () => monitor?.snooze(),
        refresh: () => { if (!compatibility?.state.required())
            monitor?.refresh(allowed, () => current.current.reload()); },
    };
}
const noSubscribe = () => () => { };
const emptySnapshot = () => null;
function isAvailable() {
    return document.visibilityState !== 'hidden' && navigator.onLine !== false;
}
function subscribeAvailability(listener) {
    document.addEventListener('visibilitychange', listener);
    window.addEventListener('online', listener);
    window.addEventListener('offline', listener);
    window.addEventListener('pageshow', listener);
    return () => {
        document.removeEventListener('visibilitychange', listener);
        window.removeEventListener('online', listener);
        window.removeEventListener('offline', listener);
        window.removeEventListener('pageshow', listener);
    };
}
/** Only called with identities already validated by buildIdentity. */
function parseIdentity(identity) {
    const [release, gitSha, builtAt] = JSON.parse(identity);
    return { release, gitSha, builtAt };
}
//# sourceMappingURL=app-update-hooks.js.map